import React from 'react';
import { Label } from '../../../../../../../components/ui/label';
import { Checkbox } from '../../../../../../../components/ui/checkbox';
import { spoolTw } from '../../spoolUiPalette';

export function CheckRow({ text, checked = false }: { text: string; checked?: boolean }) {
  return (
    <div className={spoolTw.checkRowCard}>
      <Checkbox checked={checked} className={spoolTw.checkboxProduction} />
      <span className="text-slate-900">{text}</span>
    </div>
  );
}

/** Tri-state: neutral → pass (acceptable) → fail (reject) → neutral. Click the row to cycle. */
export type TriStateCheck = 'neutral' | 'pass' | 'fail';

export function TriStateCheckRow({
  labels,
  state,
  onCycle,
  tag = 'Coating',
  failAction,
}: {
  labels: { neutral: string; pass: string; fail: string };
  state: TriStateCheck;
  onCycle: () => void;
  tag?: string;
  /** Shown on the right when `state === 'fail'` (e.g. Add Repair). Clicks do not cycle the row. */
  failAction?: React.ReactNode;
}) {
  const text = labels[state];
  const rowBase =
    'flex items-center justify-between gap-3 py-2 px-3 rounded-md border text-sm cursor-pointer select-none transition-colors';
  const rowCls =
    state === 'pass'
      ? `${rowBase} ${spoolTw.triPassRow}`
      : state === 'fail'
        ? `${rowBase} bg-red-50 border-red-400`
        : `${rowBase} bg-white border-gray-200 hover:bg-gray-50`;

  const iconBox =
    'flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] font-semibold leading-none';

  const icon =
    state === 'pass' ? (
      <span className={`${iconBox} ${spoolTw.triPassIcon}`} aria-hidden>✓</span>
    ) : state === 'fail' ? (
      <span className={`${iconBox} border-red-600 bg-red-600 text-white`} aria-hidden>✕</span>
    ) : (
      <span className={`${iconBox} border-gray-300 bg-white`} aria-hidden />
    );

  const label =
    state === 'fail'
      ? `${text}. Click to clear.`
      : state === 'pass'
        ? `${text}. Click to mark rejected.`
        : `${text}. Click to mark acceptable.`;

  return (
    <div
      role="button"
      tabIndex={0}
      className={rowCls}
      onClick={onCycle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCycle();
        }
      }}
      aria-label={label}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {icon}
        <span className={state === 'fail' ? 'text-red-800' : 'text-slate-900'}>{text}</span>
      </div>
      {state === 'fail' && failAction ? (
        <span
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {failAction}
        </span>
      ) : (
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 shrink-0">{tag}</span>
      )}
    </div>
  );
}

export function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mt-4 mb-2">
      {children}
    </h4>
  );
}

export function FormGrid({
  children,
  className,
}: {
  children: React.ReactNode;
  /** Override grid columns; default is 1 / 2 / 3 cols by breakpoint. */
  className?: string;
}) {
  return (
    <div
      className={className ?? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'}
    >
      {children}
    </div>
  );
}

/** Section heading inside weld repair form — matches kanikonna mock (navy, uppercase, letter-spacing). */
export function RepairFormSectionTitle({ children, noTopMargin }: { children: React.ReactNode; noTopMargin?: boolean }) {
  return (
    <div
      className={`text-xs font-semibold text-slate-800 uppercase tracking-[0.04em] mb-2 ${noTopMargin ? 'mt-0' : 'mt-4'}`}
    >
      {children}
    </div>
  );
}

/** Repair form field grid — `repeat(auto-fill, minmax(260px, 1fr))` like mock `.form-grid`. */
export function RepairFormGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]">
      {children}
    </div>
  );
}

/** Notice card — pale cream background, thick amber left accent, uppercase title (matches weld cutback coating mockup). */
export function StepInfoBanner({
  title,
  children,
  variant = 'notice',
  className = '',
}: {
  title: string;
  children: React.ReactNode;
  variant?: 'notice' | 'success';
  className?: string;
}) {
  const shell =
    variant === 'success'
      ? spoolTw.stepBannerSuccess
      : 'rounded-lg border border-amber-200/90 bg-amber-50 border-l-4 border-l-amber-500';
  const titleCls =
    variant === 'success'
      ? spoolTw.stepBannerSuccessTitle
      : 'text-amber-600';
  const bodyCls = variant === 'success' ? spoolTw.stepBannerSuccessBody : 'text-gray-800';

  return (
    <div className={`${shell} px-4 py-3 text-xs ${className}`}>
      <div className={`font-bold uppercase tracking-wide mb-1.5 ${titleCls}`}>{title}</div>
      <div className={`leading-relaxed ${bodyCls}`}>{children}</div>
    </div>
  );
}

export function FieldGroup({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-gray-600">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
    </div>
  );
}
